import { useQuery } from "@apollo/client";
import { addDays, isPast } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { makeStyles } from "@mui/styles";

import {
  clearActiveSubscriptionsBySubType,
  clearUserAlreadyExists,
  clearVisitCookies,
  getVisitUSState,
  resetCheckoutEditState,
  setActiveSubscriptionBySubType,
  setFollowUpVisitFlag,
  setForceFlavoredBBs,
  setRefillOptions,
} from "~/visits/utils/visitCookies";
import {
  setCookiesAndRedirectToEditPendingVisit,
  setCookiesAndRedirectToEditPharmacy,
  setCookiesAndRedirectToEditShippingAddress,
  setCookiesAndRedirectToInsomniaFollowUpVisit,
  setCookiesAndRedirectToPerformanceFollowUpVisit,
  setProperVisitCookies,
  setProperVisitCookiesForRefills,
} from "~/visits/utils/visitUtils";

import {
  kickBlue,
  kickGreen,
  kickLightBlue,
  kickLightGreen,
  kickLightPink,
  kickNavy,
  kickPink,
  kickRed,
} from "../styles/portalStyles";

import {
  setGender,
  setPhone,
  setUSState,
} from "~/patient-portal/utils/portalCookies";

import {
  ATE_SCO,
  COLOR_KICK_BLUE,
  COLOR_KICK_BLUE_LIGHTENED,
  COLOR_KICK_RED,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  DOCTOR_VISIT_STATUS_PENDING_PHOTOS,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  RX_EXPIRATION_DAYS,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";

import {
  displayFriendlyDate,
  displayFriendlyLongDate,
  displayFriendlyPrescriptionExpirationLongDate,
  displayFriendlyShortenedOrderType,
  displayOrderTypePacks,
  displaySleepPlanLabel,
  formatPhoneNumber,
  getConsultationNameFromVisitType,
  getOrderTypeImageSrc,
  getVisitTypeImageSrc,
  isAteScoCompoundedOrderType,
  isPharmacyPickupOrderType,
  preferLiveSubscription,
  subscriptionGrantsFollowUpAccess,
  supportedUSStatesForCompoundedMeds,
} from "~/shared/utils/sharedUtils";

import {
  RENDER_FAMILY_PA,
  VERTICAL_INSOMNIA,
  VERTICAL_PERFORMANCE_ANXIETY,
  activeAutoshipForVertical,
  computeInsomniaCtaState,
  computePaCtaState,
  deDupedPrescriptions,
  groupVisitsByVertical,
  refillableVisitOptions,
} from "~/patient-portal/utils/visitGrouping";

import {
  canEditPharmacy,
  fillRequestMedications,
  hasFillRequestAwaitingTracking,
  latestRefillInProgress,
  latestShippedFillRequest,
  patientLockedByDoctor,
  pendingEditHelpText,
  refillsRemainingText,
  shippingAddressForVisit,
  subscriptionPauseState,
} from "~/patient-portal/utils/treatmentCard";

import { computeMessageReplyState } from "~/patient-portal/utils/messageReplyState";

import {
  GET_DOCTOR_PATIENT_MESSAGES,
  GET_PATIENT_CARE_MESSAGES,
  GET_PATIENT_RECORD,
} from "./portalQueries";

import {
  getBlockingVisitInFamily,
  getPendingVisitInFamily,
} from "~/visits/utils/pendingVisitUtils";
import { FlavoredBBsUpsellCard } from "~/visits/utils/visitComponents";

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },
  page: {
    maxWidth: 600,
    margin: "0 auto",
    padding: "0 16px 64px",
    fontFamily: "BasisGrotesquePro-Regular",
  },
  pageTitle: {
    textAlign: "center",
    margin: "12px 0 2px",
    color: kickNavy,
  },
  greeting: {
    textAlign: "center",
    color: kickNavy,
    opacity: 0.6,
    marginBottom: 24,
    fontSize: 16,
  },
  sectionLabel: {
    fontFamily: "BasisGrotesquePro-Bold",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    fontSize: 13,
    color: kickNavy,
    opacity: 0.55,
    margin: "30px 4px 12px",
  },
  card: {
    border: "1px solid #e4e4ee",
    borderRadius: 14,
    marginBottom: 18,
    background: "#fff",
    boxShadow: "0 2px 10px 0 rgba(30,35,71,0.06)",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 12,
    },
  },
  cardHeader: {
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 19,
    color: kickNavy,
    background: kickLightBlue,
    padding: "16px 24px",
    borderBottom: "1px solid #e4e4ee",
    [theme.breakpoints.down("sm")]: {
      padding: "14px 16px",
    },
  },
  cardBody: {
    padding: "20px 24px 18px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
    },
  },
  inReviewBanner: {
    background: kickLightBlue,
    borderLeft: `3px solid ${kickBlue}`,
    borderRadius: 6,
    padding: "10px 12px",
    fontSize: 13.5,
    color: kickNavy,
    marginBottom: 14,
  },
  refillBanner: {
    background: kickLightGreen,
    borderLeft: `3px solid ${kickGreen}`,
    borderRadius: 6,
    padding: "10px 12px",
    fontSize: 13.5,
    color: kickNavy,
    marginTop: 12,
    lineHeight: 1.5,
  },
  refillBannerTitle: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickGreen,
    marginBottom: 4,
  },
  summaryRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  thumb: {
    width: 100,
    height: 100,
    borderRadius: 10,
    flexShrink: 0,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: 80,
      height: 80,
      borderRadius: 10,
    },
  },
  thumbImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: 10,
  },
  medBlock: {
    marginBottom: 8,
  },
  medName: {
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 15,
    color: kickNavy,
  },
  medMeta: {
    fontSize: 13.5,
    color: "#555",
    marginTop: 2,
  },
  fulfillment: {
    fontSize: 15,
    color: "#444",
    marginTop: 12,
    marginBottom: 4,
    lineHeight: 1.5,
    display: "inline-block",
    backgroundColor: kickLightBlue,
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: "12px 16px",
    width: 240,
    [theme.breakpoints.down("sm")]: {
      display: "block",
      margin: "12px auto",
      maxWidth: 240,
    },
  },
  fulfillmentLabel: {
    color: kickRed,
    fontFamily: "BasisGrotesquePro-Bold",
  },
  membershipNote: {
    fontSize: 13,
    color: "#666",
    marginTop: 12,
  },
  autoshipLine: {
    fontSize: 13.5,
    color: "#444",
    marginTop: 8,
  },
  ctaRow: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 16,
    [theme.breakpoints.down("sm")]: {
      alignItems: "stretch",
    },
  },
  pendingEditHelp: {
    marginTop: 14,
    fontSize: 13.5,
    color: "#555",
    lineHeight: 1.4,
  },
  upgradeLink: {
    background: "none",
    border: "none",
    color: kickBlue,
    fontFamily: "BasisGrotesquePro-Bold",
    cursor: "pointer",
    padding: 0,
    fontSize: 13.5,
    textAlign: "left",
  },
  trackingLink: {
    display: "inline-flex",
    flexDirection: "column",
    marginTop: 8,
    color: kickBlue,
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 13,
    textDecoration: "underline",
  },
  trackingPending: {
    marginTop: 8,
    color: "#777",
    fontSize: 13,
    fontStyle: "italic",
  },
  // Action Needed region
  actionCard: {
    border: "1px solid #e4e4ee",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    background: "#fff",
    boxShadow: "0 1px 4px 0 rgba(0,0,0,0.06)",
  },
  actionVertical: {
    color: kickNavy,
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 12.5,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  actionTitle: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
    fontSize: 15,
    marginTop: 2,
  },
  actionMsg: {
    fontSize: 13.5,
    color: "#555",
    margin: "6px 0 12px",
    lineHeight: 1.5,
  },
  // Explore region — compact white clinic cards (uniform across clinics)
  exploreCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    background: "#fff",
    border: "1px solid #ececf4",
    borderRadius: 16,
    padding: 16,
    marginBottom: 14,
    overflow: "hidden",
    boxShadow: "0 6px 20px -6px rgba(30,35,71,0.16)",
    // brand accent rail down the left edge
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      bottom: 0,
      width: 4,
      background: `linear-gradient(180deg, ${kickBlue} 0%, ${kickPink} 100%)`,
    },
    [theme.breakpoints.up("sm")]: {
      padding: "22px 24px 18px",
    },
  },
  exploreRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    },
  },
  exploreImg: {
    width: 76,
    height: 76,
    objectFit: "contain",
    flexShrink: 0,
    borderRadius: 14,
    background: `linear-gradient(135deg, ${kickLightBlue} 0%, ${kickLightPink} 100%)`,
    padding: 8,
    boxShadow: "0 4px 12px -4px rgba(30,35,71,0.22)",
  },
  exploreBody: {
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  exploreCardTitle: {
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 18,
    color: kickNavy,
    marginBottom: 4,
  },
  exploreTagline: {
    fontSize: 13.5,
    color: "#555",
    lineHeight: 1.45,
  },
  exploreCta: {
    alignSelf: "flex-start",
    background: `linear-gradient(135deg, ${kickBlue} 0%, ${COLOR_KICK_BLUE_LIGHTENED} 100%)`,
    boxShadow: "0 6px 16px -4px rgba(63,63,130,0.45)",
    transition: "filter 0.15s ease",
    "&:hover": {
      filter: "brightness(1.08)",
    },
    [theme.breakpoints.down("sm")]: {
      alignSelf: "stretch",
      width: "100%",
    },
  },
  emptyState: {
    textAlign: "center",
    fontSize: 17,
    color: kickNavy,
    margin: "16px 0 24px",
  },
}));

function StyledButton({ children, style, ...props }) {
  return (
    <Button
      color="primary"
      size="large"
      style={{
        backgroundColor: COLOR_KICK_BLUE,
        color: "white",
        padding: "11px 22px",
        whiteSpace: "nowrap",
        ...style,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

// ===========================================================================
// Page entry
// ===========================================================================

export default function PortalPrescriptionRefills() {
  const classes = useStyles();
  clearUserAlreadyExists();

  // Landing on the portal abandons any in-progress visit edit, so clear the transient
  // checkout/edit cookies. Without this, switching a pending visit's fulfillment (e.g. mail ->
  // local pickup) and then navigating back would leak that choice into the next "Edit Pending
  // Visit" for the same visit.
  useEffect(() => {
    resetCheckoutEditState();
  }, []);

  return (
    <main className={classes.content}>
      <PatientVisits classes={classes} />
    </main>
  );
}

function PatientVisits({ classes }) {
  const { loading, error, data } = useQuery(GET_PATIENT_RECORD);

  const patient = data?.patientRecord?.patient;
  if (patient) {
    // make sure these cookies are set to enable 1st time visits
    setPhone(patient.phone);
    setGender(patient.gender);
    setUSState(patient.usState);
  }

  // Map orderItemID -> orderItem (rebuilt from the same source each render).
  const visits = data?.patientRecord?.visits;
  const orderItemsById = useMemo(() => {
    const map = {};
    (visits || []).forEach(({ orderItems }) => {
      (orderItems || []).forEach((orderItem) => {
        map[orderItem.id] = orderItem;
      });
    });
    return map;
  }, [visits]);

  if (loading) return "Loading...";
  if (error) return `Error! ${error.message}`;

  return (
    <RefillsLayout
      classes={classes}
      patientRecord={data.patientRecord}
      orderItemsById={orderItemsById}
    />
  );
}

function RefillsLayout({ classes, patientRecord, orderItemsById }) {
  // The patient has a single care thread and a single doctor thread. These
  // queries are already warmed by the portal header (cache-first), so reading
  // them here is effectively free. Computed once and shared by every "PR"
  // action card below to decide its copy and which thread to link to.
  const { data: careMsgData } = useQuery(GET_PATIENT_CARE_MESSAGES);
  const { data: drMsgData } = useQuery(GET_DOCTOR_PATIENT_MESSAGES);
  const replyState = useMemo(
    () =>
      computeMessageReplyState(
        careMsgData?.patientCareMessages,
        drMsgData?.doctorPatientMessages,
      ),
    [careMsgData, drMsgData],
  );

  if (patientRecord == null) return null;

  const patient = patientRecord.patient;

  // Mirror the old activeSubsBySubType side effects so downstream cookie-based
  // redirects (e.g. insomnia follow-up) still see the covering subscription — active, or
  // paused but still inside its paid-through window (a scheduled pause flows like active).
  clearActiveSubscriptionsBySubType();
  patientRecord.subscriptions.forEach((sub) => {
    if (subscriptionGrantsFollowUpAccess(sub)) {
      setActiveSubscriptionBySubType(sub.subType, sub);
    }
  });

  // The sleep subscription whose pause state gates the insomnia follow-up CTA — prefer a
  // live one over an old row (active > paused > canceled; see preferLiveSubscription),
  // mirroring portalMySubscriptions. The feeding query has no ORDER BY, so this must be
  // stable regardless of row order — an active row can never be displaced by a paused one.
  const sleepSubscription = patientRecord.subscriptions
    .filter((sub) => sub.subType === SUBSCRIPTION_SUB_TYPE_SLEEP)
    .reduce(preferLiveSubscription, null);

  const { verticals, actionNeeded } = groupVisitsByVertical(
    patientRecord.visits,
  );
  // Attach each vertical's active-autoship summary (if any) so the treatment card
  // can show the next shipment date. Computed here (not in the pure grouping
  // helper) because it needs patientRecord.subscriptions, which grouping doesn't take.
  const treatmentSections = verticals
    .filter((v) => v.activeVisit || v.inReviewVisits.length > 0)
    .map((v) => ({
      ...v,
      autoship: activeAutoshipForVertical(patientRecord.subscriptions, v.key),
    }));
  const enrolledKeys = verticals.map((v) => v.key);

  return (
    <div className={classes.page}>
      <h3 className={classes.pageTitle}>Your Treatments &amp; Prescriptions</h3>
      {patient?.firstName && (
        <div className={classes.greeting}>
          Welcome back, {patient.firstName}
        </div>
      )}

      {actionNeeded.length > 0 && (
        <>
          {actionNeeded.map((visit) => (
            <ActionItemCard
              key={visit.id}
              classes={classes}
              visit={visit}
              patient={patient}
              replyState={replyState}
            />
          ))}
        </>
      )}

      {treatmentSections.length > 0 && (
        <>
          <div className={classes.sectionLabel}>Your Treatments</div>
          {treatmentSections.map((group) => (
            <TreatmentSection
              key={group.key}
              classes={classes}
              group={group}
              patient={patient}
              orderItemsById={orderItemsById}
              sleepSubscription={sleepSubscription}
              familyPendingVisit={getPendingVisitInFamily(
                patientRecord.visits,
                (group.activeVisit || group.inReviewVisits[0])?.visitType,
              )}
            />
          ))}
        </>
      )}

      {treatmentSections.length === 0 && actionNeeded.length === 0 && (
        <p className={classes.emptyState}>
          Treatments and prescriptions will appear here after your first doctor
          visit is reviewed.
        </p>
      )}

      <ExploreTreatmentsRegion
        classes={classes}
        enrolledKeys={enrolledKeys}
        visits={patientRecord.visits}
        patient={patient}
      />
    </div>
  );
}

// ===========================================================================
// Action Needed
// ===========================================================================

export function ActionItemCard({ classes, visit, patient, replyState }) {
  const history = useHistory();
  const verticalLabel = getConsultationNameFromVisitType(visit.visitType);

  let title = "";
  let message = "";
  let cta = null;

  switch (visit.status) {
    case DOCTOR_VISIT_STATUS_PENDING_PAYMENT:
    case DOCTOR_VISIT_STATUS_PENDING_PHOTOS:
      title = "Finish your visit";
      message =
        "Complete the remaining steps so a doctor can review your visit.";
      cta = (
        <StyledButton
          onClick={() => {
            if (
              visit.visitType === VISIT_TYPE_GENERALIZED_ANXIETY ||
              visit.visitType === VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP
            ) {
              setCookiesAndRedirectToSelectGAProduct(
                patient.id,
                patient.email,
                patient.phone,
                visit,
              );
            } else {
              setCookiesAndRedirectToSelectPharmacy(
                patient.id,
                patient.email,
                patient.phone,
                visit,
              );
            }
          }}
        >
          Finish Incomplete Visit&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      );
      break;
    case DOCTOR_VISIT_STATUS_PENDING_VERIFICATION:
      title = "ID verification required";
      message =
        "ID verification is still required before a doctor can review your visit.";
      cta = (
        <StyledButton
          onClick={() => {
            window.location.href =
              visit.personaHostedURL + `&redirect-uri=${window.location.href}`;
          }}
          style={{ backgroundColor: COLOR_KICK_RED }}
        >
          Complete ID Verification&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      );
      break;
    case DOCTOR_VISIT_STATUS_AWAITING_REPLY: {
      // Link to whichever thread is awaiting the patient's reply (care preferred),
      // and switch the copy once the patient's message is the latest one — the
      // "PR" status is cleared by staff, so it can lag the patient's reply.
      const replyThread = replyState?.replyThread || "care";
      if (replyState?.patientHasReplied) {
        title = "Thanks for your reply";
        message = "We've got your message and will be in touch shortly.";
      } else {
        title = "Check your messages";
        message = "We're waiting for your reply before we can move forward.";
      }
      cta = (
        <StyledButton
          onClick={() => history.push(`/me/messages/${replyThread}`)}
          style={{ backgroundColor: COLOR_KICK_RED }}
        >
          View messages&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      );
      break;
    }
    default:
      // Non-actionable statuses (e.g. "in review") are not rendered here — they
      // appear as a calm banner inside the vertical's treatment card instead.
      return null;
  }

  // Pharmacy-pickup visits keep the "Change Pharmacy" action available alongside the primary
  // action while the visit is still editable (e.g. awaiting reply / pending ID verification).
  const showChangePharmacy =
    isPharmacyPickupOrderType(visit.orderType) &&
    canEditPharmacy(visit, patient);

  return (
    <div className={classes.actionCard}>
      {verticalLabel && (
        <div className={classes.actionVertical}>{verticalLabel}</div>
      )}
      <div className={classes.actionTitle}>{title}</div>
      <div className={classes.actionMsg}>{message}</div>
      {(cta || showChangePharmacy) && (
        <div className={classes.ctaRow}>
          {cta}
          {showChangePharmacy && (
            <StyledButton
              onClick={() => setCookiesAndRedirectToEditPharmacy(visit)}
            >
              Change Pharmacy&nbsp;
              <NavigateNextIcon />
            </StyledButton>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Treatment section (one consolidated summary + one CTA cluster per vertical)
// ===========================================================================

function TreatmentSection({
  classes,
  group,
  patient,
  orderItemsById,
  sleepSubscription,
  familyPendingVisit,
}) {
  const { label, activeVisit, inReviewVisits, visitHistory } = group;
  if (!activeVisit && inReviewVisits.length === 0) return null;

  // When the most-recent visit in the vertical is the pending one, the summary
  // should reflect that pending order (it may be for a different product than
  // the last completed prescription) rather than the old completed Rx.
  const pendingVisit = inReviewVisits[0];
  const mostRecentIsPending =
    pendingVisit && visitHistory[0] && visitHistory[0].id === pendingVisit.id;

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>{label}</div>

      <div className={classes.cardBody}>
        {inReviewVisits.length > 0 && (
          <div className={classes.inReviewBanner}>
            Your visit is in review — we'll email you as soon as it's reviewed.
          </div>
        )}

        {mostRecentIsPending ? (
          <PendingVisitSummary
            classes={classes}
            group={group}
            visit={pendingVisit}
          />
        ) : (
          activeVisit && (
            <CurrentTreatmentSummary
              classes={classes}
              group={group}
              orderItemsById={orderItemsById}
            />
          )
        )}

        {inReviewVisits.length > 0 ? (
          // While a visit is pending, the only actions are to edit it (or, for INF visits whose
          // plan is already fixed, just change pharmacy/shipping address) — hide refill/follow-up
          // (they'd create a duplicate visit).
          <PendingVisitCtas
            classes={classes}
            visit={inReviewVisits[0]}
            patient={patient}
          />
        ) : (
          activeVisit && (
            <VerticalCTAs
              classes={classes}
              group={group}
              patient={patient}
              sleepSubscription={sleepSubscription}
              familyPendingVisit={familyPendingVisit}
            />
          )
        )}
      </div>
    </div>
  );
}

// PendingVisitCtas renders the action(s) for an in-review visit. INF (insomnia follow-up) visits
// have a fixed plan/order type, so — like the legacy portal — they skip the full /order-details
// edit flow and only allow changing the pharmacy (pickup) or shipping address (mail order). Every
// other type gets "Edit Pending Visit" plus, for pharmacy-pickup orders, a separate "Change
// Pharmacy" button.
export function PendingVisitCtas({ classes, visit, patient }) {
  const isINF = visit.visitType === VISIT_TYPE_INSOMNIA_FOLLOW_UP;
  const isPickup = isPharmacyPickupOrderType(visit.orderType);
  const canEditFulfillment = canEditPharmacy(visit, patient);

  // When a real doctor holds a lock on the patient, hide all self-service edit affordances
  // (the helper text, "Edit Pending Visit", and the Change Pharmacy/Shipping buttons).
  if (patientLockedByDoctor(patient)) return null;

  // INF visits have a fixed plan/order type, so — like the legacy portal — they skip the full
  // /order-details edit flow ("Edit Pending Visit" is hidden) and only allow changing the pharmacy
  // (pickup) or shipping address (mail order). No help text since there's no edit flow to explain.
  if (isINF) {
    if (!canEditFulfillment) return null;
    return (
      <div className={classes.ctaRow}>
        {isPickup ? (
          <StyledButton
            onClick={() => setCookiesAndRedirectToEditPharmacy(visit)}
          >
            Change Pharmacy&nbsp;
            <NavigateNextIcon />
          </StyledButton>
        ) : (
          <StyledButton
            onClick={() => setCookiesAndRedirectToEditShippingAddress(visit)}
          >
            Change Shipping Address&nbsp;
            <NavigateNextIcon />
          </StyledButton>
        )}
      </div>
    );
  }

  // PA/PAF/IN/GA/GAF: the full "Edit Pending Visit" flow, plus a separate "Change Pharmacy" button
  // for pharmacy-pickup orders. Help text (PA/PAF/IN only) accompanies the "Edit Pending Visit"
  // button — when there's no help text (GA/GAF) it's simply omitted.
  const helpText = pendingEditHelpText(visit);
  return (
    <>
      {helpText && <div className={classes.pendingEditHelp}>{helpText}</div>}
      <div className={classes.ctaRow}>
        <StyledButton
          onClick={() =>
            setCookiesAndRedirectToEditPendingVisit(visit, patient)
          }
        >
          Edit Pending Visit&nbsp;
          <NavigateNextIcon />
        </StyledButton>
        {isPickup && canEditFulfillment && (
          <StyledButton
            onClick={() => setCookiesAndRedirectToEditPharmacy(visit)}
          >
            Change Pharmacy&nbsp;
            <NavigateNextIcon />
          </StyledButton>
        )}
      </div>
    </>
  );
}

export function CurrentTreatmentSummary({ classes, group, orderItemsById }) {
  const { renderFamily, activeVisit, autoship } = group;
  const visit = activeVisit;

  // For an active autoship (sleep memberships today), surface the next shipment
  // date as a quiet line directly under the ship-to block. Rendered in both
  // render families; only verticals with an active autoship subscription have one.
  const autoshipLine = autoship ? (
    <div className={classes.autoshipLine}>
      Next shipment ·{" "}
      <span style={{ fontFamily: "BasisGrotesquePro-Bold" }}>
        {displayFriendlyDate(autoship.nextShipDate)}
      </span>
    </div>
  ) : null;

  if (renderFamily === RENDER_FAMILY_PA) {
    const dedupedRxs = deDupedPrescriptions(visit);
    const primaryOrderItem = orderItemsById[dedupedRxs[0]?.orderItemID];
    const thumbSrc = primaryOrderItem
      ? getOrderTypeImageSrc(
          primaryOrderItem.productSlug,
          primaryOrderItem.subcategory,
        )
      : getOrderTypeImageSrc(visit.orderType);

    // An in-progress REFILL fill request surfaces a status above the ship-to
    // block: "being processed" right after ordering, upgrading to "on the way"
    // once a tracking URL exists (tracking can lag). Gated on isRefill so the
    // original order's shipment never shows here.
    const refillFr = latestRefillInProgress(visit);
    const refillShipped = !!refillFr?.trackingURL;
    const shippingRxs = refillFr ? fillRequestMedications(visit, refillFr) : [];

    return (
      <>
        <div className={classes.summaryRow}>
          <div className={classes.thumb}>
            <img className={classes.thumbImg} src={thumbSrc} alt="" />
          </div>
          <div>
            {dedupedRxs.map((rx) => {
              const orderItem = orderItemsById[rx.orderItemID];
              const packLabel = orderItem
                ? displayFriendlyShortenedOrderType(
                    orderItem.productSlug,
                    orderItem.subcategory,
                  )
                : displayFriendlyShortenedOrderType(visit.orderType);
              const expired =
                isPast(
                  addDays(new Date(rx.createdAt), RX_EXPIRATION_DAYS),
                ) || rx.refillsCanceled;
              const refillsText = refillsRemainingText(visit, rx);
              return (
                <div className={classes.medBlock} key={rx.id}>
                  <div className={classes.medName}>
                    {rx.medicationName} {rx.medicationDosage}
                    {packLabel ? ` · ${packLabel}` : ""}
                  </div>
                  <div className={classes.medMeta}>
                    {expired ? "Expired " : "Expires "}
                    {displayFriendlyPrescriptionExpirationLongDate(
                      visit.visitDate,
                    )}
                    {refillsText ? ` · ${refillsText}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {refillFr && (
          <div className={classes.refillBanner}>
            <div className={classes.refillBannerTitle}>
              {refillShipped
                ? "Your refill is on the way"
                : "Your refill is being processed"}
            </div>
            {shippingRxs.map((rx) => {
              const orderItem = orderItemsById[rx.orderItemID];
              const packLabel = orderItem
                ? displayFriendlyShortenedOrderType(
                    orderItem.productSlug,
                    orderItem.subcategory,
                  )
                : displayFriendlyShortenedOrderType(visit.orderType);
              return (
                <div key={rx.id}>
                  {rx.medicationName} {rx.medicationDosage}
                  {packLabel ? ` · ${packLabel}` : ""}
                </div>
              );
            })}
          </div>
        )}
        <ShippingOrPharmacyBlock
          classes={classes}
          visit={visit}
          refillInProgress={refillFr}
        />
        {autoshipLine}
      </>
    );
  }

  // Insomnia family
  return (
    <>
      <div className={classes.summaryRow}>
        <div className={classes.thumb}>
          <img
            className={classes.thumbImg}
            src={getVisitTypeImageSrc(visit.visitType)}
            alt=""
          />
        </div>
        <div>
          {(visit.visitPrescriptions || []).map((p) => (
            <div className={classes.medName} key={p.id}>
              {p.medicationName} {p.medicationDosage}
            </div>
          ))}
          <div className={classes.medMeta}>
            Prescribed {displayFriendlyLongDate(visit.visitDate)}
          </div>
        </div>
      </div>
      <ShippingOrPharmacyBlock classes={classes} visit={visit} />
      {autoshipLine}
      <div className={classes.membershipNote}>
        Adjust sleep meds or refill anytime — included with membership at no
        extra cost.
      </div>
    </>
  );
}

export function PendingVisitSummary({ classes, group, visit }) {
  // Pending (in-review) visits carry order items but not finalized
  // prescriptions, so render the ordered product(s) (image + label) rather than
  // Rx details — medication/dosage/refills aren't set until a doctor reviews.
  // One row per order item (main product + add-ons), main products listed first,
  // each with its own product image.
  const orderItems = [...(visit.orderItems || [])].sort(
    (a, b) => (a.isAddon ? 1 : 0) - (b.isAddon ? 1 : 0),
  );
  const isPA = group.renderFamily === RENDER_FAMILY_PA;
  const lineImageSrc = (oi) =>
    isPA
      ? getOrderTypeImageSrc(
          oi?.productSlug || visit.orderType,
          oi?.subcategory,
        )
      : getVisitTypeImageSrc(visit.visitType);
  // PA items show their pack label; sleep items show their plan cadence
  // ("Monthly/Quarterly Prescription Sleep Plan") rather than a pack label, or
  // "Prescription Sleep Follow-up" when the visit is an insomnia follow-up.
  const isInsomniaFollowUp = visit.visitType === VISIT_TYPE_INSOMNIA_FOLLOW_UP;
  const lineLabel = (orderType, subcategory) =>
    (isPA
      ? displayOrderTypePacks(orderType, subcategory)
      : displaySleepPlanLabel(visit.orderType, isInsomniaFollowUp)) ||
    group.label;

  const productLines =
    orderItems.length > 0
      ? orderItems.map((oi) => ({
          key: oi.id,
          imgSrc: lineImageSrc(oi),
          text: lineLabel(oi.productSlug, oi.subcategory),
        }))
      : [
          {
            key: visit.id,
            imgSrc: lineImageSrc(null),
            text: lineLabel(visit.orderType),
          },
        ];

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {productLines.map((line) => (
          <div
            className={classes.summaryRow}
            key={line.key}
            style={{ alignItems: "center" }}
          >
            <div className={classes.thumb}>
              <img className={classes.thumbImg} src={line.imgSrc} alt="" />
            </div>
            <div className={classes.medName}>{line.text}</div>
          </div>
        ))}
      </div>
      <ShippingOrPharmacyBlock classes={classes} visit={visit} pending />
    </>
  );
}

export function VerticalCTAs({
  classes,
  group,
  patient,
  sleepSubscription,
  familyPendingVisit,
}) {
  const [showPharmacyModal, setShowPharmacyModal] = useState(false);
  const { renderFamily, activeVisit } = group;

  // Follow-up CTAs would start a new visit in a clinical family that already has one in
  // progress (possibly in another vertical group, e.g. a pending GA visit vs this PA card) —
  // the questionnaire's family guard bounces those straight back to the portal. Refill CTAs
  // stay available: a refill reorders an approved prescription and creates no new visit.
  const suppressFollowUps = !!familyPendingVisit;

  if (renderFamily !== RENDER_FAMILY_PA) {
    const cta = computeInsomniaCtaState(activeVisit);
    if (!cta?.showFollowUpCta || suppressFollowUps) return null;
    return (
      <div className={classes.ctaRow}>
        <StyledButton
          onClick={() => {
            // Same gate as the subscription card's follow-up CTA: only a functionally
            // paused membership (paid-through window lapsed) needs the resume-and-charge
            // confirm; a merely scheduled pause flows like an active membership.
            if (
              subscriptionPauseState(sleepSubscription) === "paused" &&
              !confirm(
                "Completing a follow-up visit will resume your subscription and you'll be charged to restart it. Proceed?",
              )
            ) {
              return;
            }
            setCookiesAndRedirectToInsomniaFollowUpVisit(patient, activeVisit);
          }}
        >
          Start Follow-up Visit&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      </div>
    );
  }

  const cta = computePaCtaState(activeVisit);
  if (!cta) return null;
  const {
    prescription,
    doRefill,
    showCta,
    followUpNeedsPharmacyConfirm,
    showUpgradeTo2Pack,
  } = cta;

  return (
    <div className={classes.ctaRow}>
      {showCta && (doRefill || !suppressFollowUps) && (
        <StyledButton
          onClick={() => {
            if (followUpNeedsPharmacyConfirm) {
              setShowPharmacyModal(true);
            } else if (doRefill) {
              setCookiesAndRedirectToPerformanceVisitPaymentRefill(
                patient,
                activeVisit,
                prescription,
                refillableVisitOptions(group.visitHistory, group.renderFamily),
              );
            } else {
              setCookiesAndRedirectToPerformanceFollowUpVisit(
                patient,
                prescription,
              );
            }
          }}
        >
          {doRefill ? "Order a Refill" : "Quick Follow-up Visit"}&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      )}

      {showUpgradeTo2Pack && !suppressFollowUps && (
        <button
          type="button"
          className={classes.upgradeLink}
          onClick={() =>
            setCookiesAndRedirectToPerformanceFollowUpVisit(
              patient,
              prescription,
              ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
              true,
            )
          }
        >
          Upgrade to 2-pack (double your supply) &rsaquo;
        </button>
      )}

      <Dialog
        open={showPharmacyModal}
        onClose={() => setShowPharmacyModal(false)}
        aria-labelledby="pharmacy-alert-dialog-title"
        aria-describedby="pharmacy-alert-dialog-description"
      >
        <DialogTitle id="pharmacy-alert-dialog-title">
          Have you already used the included refill?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="pharmacy-alert-dialog-description">
            Your prescription comes with 1 refill. You can get it by contacting
            your pharmacy directly by calling them or ordering from their
            website. Would you still like to proceed with a new follow-up visit?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPharmacyModal(false)} color="primary">
            Cancel
          </Button>
          <Button
            color="primary"
            size="large"
            style={{ backgroundColor: COLOR_KICK_BLUE, color: "white" }}
            onClick={() => {
              setShowPharmacyModal(false);
              setCookiesAndRedirectToPerformanceFollowUpVisit(
                patient,
                prescription,
              );
            }}
            autoFocus
          >
            Proceed
            <NavigateNextIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// Fulfillment (shipping address or pharmacy pickup) — compact single block.
// Pure render from props; the editable "Change Pharmacy" action lives on the card's CTA row.
// ===========================================================================

export function ShippingOrPharmacyBlock({
  classes,
  visit,
  pending = false,
  refillInProgress = null,
}) {
  if (isPharmacyPickupOrderType(visit.orderType)) {
    const pharmacyInfo = visit.pharmacyInfo;
    if (!pharmacyInfo) return null;

    return (
      <div className={classes.fulfillment}>
        <div className={classes.fulfillmentLabel}>
          {pending ? "Local Pickup Pharmacy:" : "Pickup at:"}
        </div>
        {pharmacyInfo.pharmacyName && (
          <div style={{ fontFamily: "BasisGrotesquePro-Bold" }}>
            {pharmacyInfo.pharmacyName}
          </div>
        )}
        {(pharmacyInfo.pharmacyAddress1 || pharmacyInfo.pharmacyAddress2) && (
          <div>
            {[pharmacyInfo.pharmacyAddress1, pharmacyInfo.pharmacyAddress2]
              .filter(Boolean)
              .join(" ")}
          </div>
        )}
        {pharmacyInfo.pharmacyCity && (
          <div>
            {pharmacyInfo.pharmacyCity}, {pharmacyInfo.pharmacyState}{" "}
            {pharmacyInfo.pharmacyZip}
          </div>
        )}
        {pharmacyInfo.pharmacyPhone && (
          <div>
            <a
              href={`tel:${pharmacyInfo.pharmacyPhone.replace(/\D/g, "")}`}
              style={{ color: kickBlue, textDecoration: "none" }}
            >
              {formatPhoneNumber(pharmacyInfo.pharmacyPhone)}
            </a>
          </div>
        )}
      </div>
    );
  }

  // Prefer the address from the latest order payment (a refill can ship to a
  // different address than the original visit); fall back to the visit address.
  const ship = shippingAddressForVisit(visit);
  if (!ship.address1 && !ship.city) return null;

  // While a refill is in progress, the tracking link must reflect THAT refill —
  // not the prior (original-order) shipment. Use the refill's fill request once
  // it has tracking, otherwise a "coming soon" note; only fall back to the
  // visit's most recent shipment when no refill is in progress.
  const trackedFr = refillInProgress
    ? refillInProgress.trackingURL
      ? refillInProgress
      : null
    : latestShippedFillRequest(visit);
  const trackingURL = trackedFr ? trackedFr.trackingURL : "";
  const trackingNumber = trackedFr ? trackedFr.trackingNumber : "";

  // Show "Tracking link coming soon" whenever a mail-order shipment has been set
  // in motion but a tracking link hasn't landed yet (tracking can lag the fill by
  // a day or two). This covers an in-progress refill as well as the original
  // order's shipment — previously only refills surfaced this note.
  const awaitingTracking =
    !trackingURL &&
    (!!refillInProgress || hasFillRequestAwaitingTracking(visit));

  return (
    <div className={classes.fulfillment}>
      <div className={classes.fulfillmentLabel}>Ships to:</div>
      {(ship.address1 || ship.address2) && (
        <div>{[ship.address1, ship.address2].filter(Boolean).join(" ")}</div>
      )}
      {ship.city && (
        <div>
          {ship.city}, {ship.usState} {ship.zip}
        </div>
      )}
      {trackingURL ? (
        <a
          className={classes.trackingLink}
          href={trackingURL}
          target="_blank"
          rel="noreferrer"
        >
          <span>Track shipment&nbsp;&rsaquo;</span>
          {trackingNumber && <span>{trackingNumber}</span>}
        </a>
      ) : (
        awaitingTracking && (
          <div className={classes.trackingPending}>
            Tracking link coming soon
          </div>
        )
      )}
    </div>
  );
}

// ===========================================================================
// Explore other treatments (verticals the patient isn't enrolled in + upsells)
// ===========================================================================

const EXPLORE_VERTICALS = [
  {
    key: VERTICAL_PERFORMANCE_ANXIETY,
    visitType: VISIT_TYPE_PERFORMANCE_ANXIETY,
    title: "Performance Anxiety",
    tagline:
      "Beat performance anxiety with doctor-prescribed beta blockers — calm for presentations, interviews & big moments.",
    image: getOrderTypeImageSrc(ORDER_TYPE_PROP_1_PACK_FLAVORED),
    startHref: "/visits/pac/1",
  },
  {
    key: VERTICAL_INSOMNIA,
    visitType: VISIT_TYPE_INSOMNIA,
    title: "Performance Sleep Treatments",
    tagline:
      "Doctor-prescribed sleep care from an expert Stanford sleep doctor. Fall asleep faster, stay asleep longer, and wake up truly rested.",
    image: getVisitTypeImageSrc(VISIT_TYPE_INSOMNIA),
    startHref: "/visits/inc/1",
  },
];

function shouldShowBuyFlavoredBBs(visits) {
  if (!supportedUSStatesForCompoundedMeds.includes(getVisitUSState())) {
    return false;
  }
  const hasAteScoOrder = visits.some((visit) =>
    isAteScoCompoundedOrderType(visit.orderType),
  );
  const hasClosedFlavoredBBVisit = visits.some(
    (visit) =>
      visit.visitType.includes(VISIT_TYPE_PERFORMANCE_ANXIETY) &&
      visit.orderType.includes("flavored") &&
      visit.status === DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  );
  const hasInitialBBVisitPendingPayment = visits.some(
    (visit) =>
      visit.visitType === VISIT_TYPE_PERFORMANCE_ANXIETY &&
      visit.status === DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  );
  // Hide the upsell when a visit in the anxiety family (PA/PAF/GA/GAF) would BLOCK a new
  // checkout (P/R/PRAD/PR) — starting a new flavored BB visit would be bounced straight back
  // here by the questionnaire's family guard (and 422'd by the checkout backend guard). A visit
  // stalled in Pending Verification does NOT hide it: the new checkout supersedes it.
  const hasPendingAnxietyFamilyVisit = !!getBlockingVisitInFamily(
    visits,
    VISIT_TYPE_PERFORMANCE_ANXIETY,
  );
  if (
    hasAteScoOrder ||
    hasClosedFlavoredBBVisit ||
    hasInitialBBVisitPendingPayment ||
    hasPendingAnxietyFamilyVisit
  ) {
    return false;
  }
  return true;
}

function ExploreTreatmentsRegion({ classes, enrolledKeys, visits, patient }) {
  const notEnrolled = EXPLORE_VERTICALS.filter(
    (v) =>
      !enrolledKeys.includes(v.key) &&
      // Don't advertise a "Start Visit" the questionnaire's family guard would immediately
      // bounce — e.g. a pending GA visit blocks starting PA (same anxiety family). A visit
      // stalled in Pending Verification does NOT hide the card: the funnel guard lets the
      // patient start over and the new checkout supersedes the stalled visit. Without this,
      // a >45-day PV visit is hidden from the portal entirely yet still removes the only
      // "start a visit" entry point — an invisible dead end.
      !getBlockingVisitInFamily(visits, v.visitType),
  );
  const showFlavoredBBs =
    enrolledKeys.includes(VERTICAL_PERFORMANCE_ANXIETY) &&
    shouldShowBuyFlavoredBBs(visits);

  if (notEnrolled.length === 0 && !showFlavoredBBs) return null;

  return (
    <>
      <div className={classes.sectionLabel}>Explore our treatments</div>

      {notEnrolled.map((v) => (
        <div key={v.key} className={classes.exploreCard}>
          <div className={classes.exploreRow}>
            <img className={classes.exploreImg} src={v.image} alt="" />
            <div className={classes.exploreBody}>
              <div className={classes.exploreCardTitle}>{v.title}</div>
              <div className={classes.exploreTagline}>{v.tagline}</div>
            </div>
          </div>
          <StyledButton
            className={classes.exploreCta}
            onClick={() => {
              clearVisitCookies();
              window.location.href = v.startHref;
            }}
          >
            Start Visit&nbsp;
            <NavigateNextIcon />
          </StyledButton>
        </div>
      ))}

      {showFlavoredBBs && (
        <FlavoredBBsUpsellCard
          onClick={() => {
            const hasAnyClosedInitialPAVisits = visits.some(
              (visit) =>
                visit.visitType === VISIT_TYPE_PERFORMANCE_ANXIETY &&
                visit.status === DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
            );
            const checkoutSessionID = uuidv4();
            setForceFlavoredBBs(true, checkoutSessionID);
            const consultationType = hasAnyClosedInitialPAVisits
              ? CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP
              : CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;

            if (hasAnyClosedInitialPAVisits) {
              setFollowUpVisitFlag("yes", checkoutSessionID);
            }

            setProperVisitCookies(
              patient.id,
              "",
              "",
              patient.email,
              patient.phone,
              patient.address1,
              patient.address2,
              patient.city,
              patient.usState,
              patient.zip,
              checkoutSessionID,
            );

            window.location.href = `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`;
          }}
        />
      )}
    </>
  );
}

// ===========================================================================
// Redirect helpers (unchanged behavior — reuse the shared visitUtils flows)
// ===========================================================================

function setCookiesAndRedirectToPerformanceVisitPaymentRefill(
  patient,
  visit,
  prescription,
  options = null,
) {
  const checkoutSessionID = uuidv4();
  setProperVisitCookiesForRefills(
    patient,
    visit,
    prescription,
    checkoutSessionID,
  );
  // Persist the cross-visit refill options for the refill-make-changes picker.
  // Must happen AFTER setProperVisitCookiesForRefills: its clearVisitCookies()
  // wipes every checkout session from localStorage.
  if (options?.length) {
    const { id, email, phone, address1, address2, city, usState, zip } =
      patient;
    setRefillOptions(
      {
        patient: { id, email, phone, address1, address2, city, usState, zip },
        options,
      },
      checkoutSessionID,
    );
  }
  const hasGAOrderType = visit.orderItems?.some((orderItem) =>
    orderItem.productSlug.includes(ATE_SCO),
  );
  const consultationType = hasGAOrderType
    ? CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP
    : CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
  window.location.href = `/visits/${consultationType}/refill-make-changes?checkoutSessionID=${checkoutSessionID}`;
}

function setCookiesAndRedirectToSelectGAProduct(userID, email, phone, visit) {
  const checkoutSessionID = uuidv4();
  setProperVisitCookies(
    userID,
    visit.id,
    "",
    email,
    phone,
    "",
    "",
    "",
    "",
    "",
    checkoutSessionID,
  );
  const isGAFollowUpVisit =
    visit.visitType === VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP;
  setFollowUpVisitFlag(isGAFollowUpVisit ? "yes" : "", checkoutSessionID);

  let consultationType =
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP;
  if (!isGAFollowUpVisit) {
    consultationType = CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY;
  }
  window.location.href = `/visits/${consultationType}/select-ga-product?checkoutSessionID=${checkoutSessionID}`;
}

function setCookiesAndRedirectToSelectPharmacy(userID, email, phone, visit) {
  const checkoutSessionID = uuidv4();
  setProperVisitCookies(
    userID,
    visit.id,
    "",
    email,
    phone,
    "",
    "",
    "",
    "",
    "",
    checkoutSessionID,
  );
  setFollowUpVisitFlag(
    [
      VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
      VISIT_TYPE_INSOMNIA_FOLLOW_UP,
    ].includes(visit.visitType)
      ? "yes"
      : "",
    checkoutSessionID,
  );

  let consultationType;

  switch (visit.visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
      break;
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      consultationType =
        CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
      break;
    case VISIT_TYPE_INSOMNIA:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_INSOMNIA;
      break;
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP;
      break;
    default:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
  }

  window.location.href = `/visits/${consultationType}/select-pharmacy?checkoutSessionID=${checkoutSessionID}`;
}
